import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { verifyMyPassword } from '../../lib/verifyPassword';
import {
  Building2, Plus, ChevronRight, ChevronLeft, Trash2, Edit3, Users,
  BookOpen, Save, X, Search, Layers, UserPlus, Upload, CheckCircle, XCircle,
  Mail, Hash, ClipboardCopy, AlertCircle, GraduationCap, Filter, Info, Phone,
  Calendar, Key, Clock, ChevronUp, ChevronDown, ArrowUpDown, ShieldCheck, Lock, Shield, RefreshCw
} from '../DeskeyIcons';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ClassSubjectsTab } from '@/SubjectManagement';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { ProfileCard } from '@/components/ProfileCard';
import { notifyError, friendlyError } from '../../lib/errors';
import { confirmDialog } from '../ConfirmDialog';
import { institutionApi } from '../../lib/institutionApi';
import { DeptCardsSkeleton, BigCardsSkeleton, PersonResultsSkeleton, StudentTableSkeleton } from './InstitutionSkeletons';

/* ── Shared sub-components (same design as StudentsSection) ── */
const StatusBadge = ({ status }) => {
  const styles = {
    active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    pending: 'bg-amber-50 text-amber-700 border-amber-200',
    none: 'bg-gray-50 text-gray-400 border-gray-200',
  };
  return (
    <span className={`text-xs px-2.5 py-1 rounded-full font-semibold border ${styles[status] || styles.none}`}>
      {status === 'active' ? 'Active' : status === 'pending' ? 'Pending' : 'No Licence'}
    </span>
  );
};

const ModalInfoRow = ({ icon: Icon, label, value, accent, mono }) => (
  <div className="flex items-center gap-3 py-3">
    <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${accent}10` }}>
      <Icon className="w-4 h-4" style={{ color: accent }} />
    </div>
    <div className="min-w-0 flex-1">
      <p className="text-xs text-gray-400 font-medium">{label}</p>
      <p className={`text-sm text-gray-800 truncate ${mono ? 'font-mono text-xs' : ''}`}>{value}</p>
    </div>
  </div>
);

const StudentProfileModal = ({ person, accent, onClose }) => {
  if (!person) return null;
  const A = accent;
  const initial = (person.full_name || person.student_name || '?')[0].toUpperCase();
  const name = person.full_name || person.student_name || 'Unknown';
  const email = person.email || person.student_email || '';
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { weekday: 'short', month: 'long', day: 'numeric', year: 'numeric' }) : '—';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="student-profile-modal">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        <div className="h-28 relative" style={{ background: `linear-gradient(135deg, ${A}, ${A}cc)` }}>
          <button onClick={onClose} className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors" data-testid="close-student-profile-modal">
            <X className="w-4 h-4 text-white" />
          </button>
        </div>
        <div className="flex justify-center -mt-14">
          {person.profile_photo ? (
            <img src={person.profile_photo} alt="" className="w-28 h-28 rounded-full border-4 border-white shadow-lg object-cover" />
          ) : (
            <div className="w-28 h-28 rounded-full border-4 border-white shadow-lg flex items-center justify-center text-white text-3xl font-semibold" style={{ background: A }}>
              {initial}
            </div>
          )}
        </div>
        <div className="text-center mt-3 px-6">
          <h3 className="text-xl font-semibold text-gray-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{name}</h3>
          <p className="text-xs text-gray-400 mt-0.5 font-medium">Student</p>
          {person.licence_status && <StatusBadge status={person.licence_status} />}
        </div>
        {(person.bio) && (
          <div className="px-6 mt-4">
            <p className="text-sm text-gray-500 text-center leading-relaxed">{person.bio}</p>
          </div>
        )}
        <div className="px-6 mt-5 space-y-0 divide-y divide-gray-100">
          <ModalInfoRow icon={Mail} label="Email" value={email} accent={A} />
          {person.phone && <ModalInfoRow icon={Phone} label="Phone" value={person.phone} accent={A} />}
          {person.department_names && <ModalInfoRow icon={Building2} label="Departments" value={person.department_names} accent={A} />}
          {person.enrolled_classes !== undefined && <ModalInfoRow icon={BookOpen} label="Enrolled classes" value={`${person.enrolled_classes || 0} class${(person.enrolled_classes || 0) !== 1 ? 'es' : ''}`} accent={A} />}
          {person.created_at && <ModalInfoRow icon={Calendar} label="Joined" value={formatDate(person.created_at)} accent={A} />}
          {person.licence_key && <ModalInfoRow icon={Key} label="Licence key" value={person.licence_key} accent={A} mono />}
        </div>
        {(person.departments || []).length > 0 && (
          <div className="px-6 py-5">
            <p className="text-xs font-semibold text-gray-400 mb-2">Departments</p>
            <div className="flex flex-wrap gap-1.5">
              {person.departments.map(d => (
                <span key={d.id} className="text-xs px-3 py-1 rounded-lg font-medium bg-violet-50 text-violet-600 border border-violet-100">{d.name}</span>
              ))}
            </div>
          </div>
        )}
        {!(person.departments || []).length && <div className="pb-5" />}
      </div>
    </div>
  );
};

const API_URL = process.env.REACT_APP_BACKEND_URL;
const CODE_FORMAT_RE = /^[A-Z]{3}[0-9]{4}$/;

const api = institutionApi;


export default function DepartmentsSection({ accent, showToast }) {
  const A = accent;

  // Navigation state — list | detail | batch | class
  const [view, setView] = useState('list');
  const [selectedDept, setSelectedDept] = useState(null);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [selectedClass, setSelectedClass] = useState(null);
  const [classTab, setClassTab] = useState('students');
  const [showClassTeacherModal, setShowClassTeacherModal] = useState(false);
  const [classTeacherQuery, setClassTeacherQuery] = useState('');
  const [classTeacherResults, setClassTeacherResults] = useState([]);
  const [classTeacherSearching, setClassTeacherSearching] = useState(false);
  const [assigningClassTeacher, setAssigningClassTeacher] = useState(null);
  const [showUnassign2FA, setShowUnassign2FA] = useState(false);
  useEffect(() => {
    if (!showClassTeacherModal) return;
    setClassTeacherSearching(true);
    const t = setTimeout(() => {
      api(`/teachers/search?q=${encodeURIComponent(classTeacherQuery)}`).then(d => { setClassTeacherResults(d || []); setClassTeacherSearching(false); })
        .catch(e => { notifyError(e, "Couldn't search teachers."); setClassTeacherSearching(false); });
    }, 300);
    return () => clearTimeout(t);
  }, [showClassTeacherModal, classTeacherQuery]);
  const [unassignPwd, setUnassignPwd] = useState('');
  const [unassigning, setUnassigning] = useState(false);
  const [teacherInfoPopup, setTeacherInfoPopup] = useState(null);
  // Class substitute state
  const [showSubModal, setShowSubModal] = useState(false);
  const [subStep, setSubStep] = useState(1);
  const [subQuery, setSubQuery] = useState('');
  const [subResults, setSubResults] = useState([]);
  const [subSearching, setSubSearching] = useState(false);
  const [subSelected, setSubSelected] = useState(null);
  const [subStartDate, setSubStartDate] = useState('');
  const [subEndDate, setSubEndDate] = useState('');
  const [subReason, setSubReason] = useState('');
  const [subConfirmPwd, setSubConfirmPwd] = useState('');
  const [subAssigning, setSubAssigning] = useState(false);
  const [subError, setSubError] = useState('');
  const [classSubs, setClassSubs] = useState([]);
  const [showUnassignSub2FA, setShowUnassignSub2FA] = useState(null);
  const [unassignSubPwd, setUnassignSubPwd] = useState('');
  const [unassigningSub, setUnassigningSub] = useState(false);

  // Data
  const [departments, setDepartments] = useState([]);
  const [batches, setBatches] = useState([]);
  const [classes, setClasses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [opening, setOpening] = useState(false); // drilling into a branch / year / classroom
  const [searchQuery, setSearchQuery] = useState('');

  // Department form
  const [showDeptForm, setShowDeptForm] = useState(false);
  const [deptForm, setDeptForm] = useState({ name: '', code: '', head_teacher_id: '' });
  const [editingDept, setEditingDept] = useState(null);
  const [codeTouched, setCodeTouched] = useState(false);
  const [codeStatus, setCodeStatus] = useState(null); // null | 'checking' | 'available' | 'taken'

  // Batch form
  const [showBatchForm, setShowBatchForm] = useState(false);
  const [batchForm, setBatchForm] = useState({ name: '', code: '', year: '' });
  const [batchFromYear, setBatchFromYear] = useState('');
  const [batchToYear, setBatchToYear] = useState('');
  const [batchCodeTouched, setBatchCodeTouched] = useState(false);
  const [batchCodeStatus, setBatchCodeStatus] = useState(null); // null | 'checking' | 'available' | 'taken' | 'invalid'

  // Class form
  const [showClassForm, setShowClassForm] = useState(false);
  const [classForm, setClassForm] = useState({ name: '', code: '', capacity: 30 });
  const [classCodeTouched, setClassCodeTouched] = useState(false);
  const [classCodeStatus, setClassCodeStatus] = useState(null); // null | 'checking' | 'available' | 'taken' | 'invalid'

  // Students tab state
  const [classStudents, setClassStudents] = useState([]);
  const [pendingEnrollments, setPendingEnrollments] = useState([]);
  const [availableStudents, setAvailableStudents] = useState([]);
  const [addMode, setAddMode] = useState(null);          // null | email | csv | available
  const [emailInput, setEmailInput] = useState('');
  const [csvInput, setCsvInput] = useState('');
  const [selectedAvailable, setSelectedAvailable] = useState([]);
  const [enrollLoading, setEnrollLoading] = useState(false);

  // Department admin management
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [adminForm, setAdminForm] = useState({ email: '', password: '', full_name: '' });
  const [adminCreating, setAdminCreating] = useState(false);
  const [deptAdmin, setDeptAdmin] = useState(null);
  const [editingAdmin, setEditingAdmin] = useState(false);
  const [resetPasswordForm, setResetPasswordForm] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // ─── DATA LOADERS ─────────────────────────────────────────────────────────
  const loadDepartments = useCallback(async () => {
    setLoading(true);
    try {
      const d = await api('/institution/departments');
      setDepartments(d.departments || []);
    } catch (e) { notifyError(e, "Couldn't load departments."); }
    setLoading(false);
  }, []);

  const loadTeachers = useCallback(async () => {
    const d = await api('/institution/teachers');
    setTeachers(d.teachers || []);
  }, []);

  useEffect(() => { loadDepartments(); loadTeachers(); }, [loadDepartments, loadTeachers]);

  const [deptSortBy, setDeptSortBy] = useState('name');
  const [deptFilter, setDeptFilter] = useState('all'); // all | has_head | no_head
  const [classSearch, setClassSearch] = useState('');
  const [classSortBy, setClassSortBy] = useState('name');
  const [selectedStudentProfile, setSelectedStudentProfile] = useState(null);
  const [classStudentSearch, setClassStudentSearch] = useState('');

  const filteredDepts = useMemo(() => {
    let list = [...departments];
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(d => d.name.toLowerCase().includes(q) || d.code.toLowerCase().includes(q));
    }
    if (deptFilter === 'has_head') list = list.filter(d => d.head_teacher_name);
    else if (deptFilter === 'no_head') list = list.filter(d => !d.head_teacher_name);
    list.sort((a, b) => {
      if (deptSortBy === 'name') return (a.name || '').localeCompare(b.name || '');
      if (deptSortBy === 'classes') return (b.class_count || 0) - (a.class_count || 0);
      if (deptSortBy === 'students') return (b.student_count || 0) - (a.student_count || 0);
      return 0;
    });
    return list;
  }, [departments, searchQuery, deptFilter, deptSortBy]);

  const filteredClasses = useMemo(() => {
    let list = [...classes];
    if (classSearch.trim()) {
      const q = classSearch.toLowerCase();
      list = list.filter(c => c.name.toLowerCase().includes(q) || (c.code || '').toLowerCase().includes(q) || (c.class_code || '').toLowerCase().includes(q));
    }
    list.sort((a, b) => {
      if (classSortBy === 'name') return (a.name || '').localeCompare(b.name || '');
      if (classSortBy === 'students') return (b.student_count || 0) - (a.student_count || 0);
      if (classSortBy === 'pending') return (b.pending_count || 0) - (a.pending_count || 0);
      return 0;
    });
    return list;
  }, [classes, classSearch, classSortBy]);

  const filteredClassStudents = useMemo(() => {
    if (!classStudentSearch.trim()) return classStudents;
    const q = classStudentSearch.toLowerCase();
    return classStudents.filter(s => (s.student_name || '').toLowerCase().includes(q) || (s.student_email || '').toLowerCase().includes(q));
  }, [classStudents, classStudentSearch]);

  const totals = useMemo(() => ({
    depts: departments.length,
    classes: departments.reduce((s, d) => s + (d.class_count || 0), 0),
    students: departments.reduce((s, d) => s + (d.student_count || 0), 0),
  }), [departments]);

  // Open department → shows batches
  const openDept = async (dept) => {
    setSelectedDept(dept);
    setView('detail');
    setShowAdminPanel(false);
    setEditingAdmin(false);
    setDeptAdmin(null);
    setSelectedBatch(null);
    setBatches([]);  // never show the previous branch's years while this one loads
    setOpening(true);
    try {
      const d = await api(`/institution/departments/${dept.id}/batches`);
      setBatches(d.batches || []);
    } catch (e) { notifyError(e, "Couldn't load batches."); }
    setOpening(false);
    // Load dept admin info
    try {
      const adminData = await api(`/institution/departments/${dept.id}/admin`);
      setDeptAdmin(adminData.admin || null);
    } catch (e) { console.error(e); }
  };

  // Open batch → shows classes inside it
  const openBatch = async (batch) => {
    setSelectedBatch(batch);
    setView('batch');
    setClasses([]);
    setOpening(true);
    try {
      const d = await api(`/institution/departments/${selectedDept.id}/classes?batch_id=${batch.id}`);
      setClasses(d.classes || []);
    } catch (e) { notifyError(e, "Couldn't load classes."); }
    setOpening(false);
  };

  // Keep batchForm.year synced to the From/To dropdowns, e.g. 2025 & 2026 -> "2025-26".
  useEffect(() => {
    if (!batchFromYear || !batchToYear) return;
    setBatchForm(p => ({ ...p, year: `${batchFromYear}-${String(batchToYear).slice(-2)}` }));
  }, [batchFromYear, batchToYear]);

  // Suggest a random unique code once when the form opens, unless the user
  // has already typed into the code field themselves.
  useEffect(() => {
    if (!showBatchForm || batchCodeTouched) return;
    (async () => {
      try {
        const r = await api('/institution/batches/suggest-code');
        if (r?.code) { setBatchForm(p => ({ ...p, code: r.code })); setBatchCodeStatus('available'); }
      } catch { /* best-effort suggestion */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showBatchForm, batchCodeTouched]);

  // Once the user edits the code directly, validate format then check availability.
  useEffect(() => {
    if (!showBatchForm || !batchCodeTouched) return;
    const code = batchForm.code.trim();
    if (!code) { setBatchCodeStatus(null); return; }
    if (!CODE_FORMAT_RE.test(code)) { setBatchCodeStatus('invalid'); return; }
    setBatchCodeStatus('checking');
    const t = setTimeout(async () => {
      try {
        const r = await api(`/institution/batches/check-code?code=${encodeURIComponent(code)}`);
        setBatchCodeStatus(r?.available ? 'available' : 'taken');
      } catch { setBatchCodeStatus(null); }
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchForm.code, batchCodeTouched, showBatchForm]);

  // Save batch
  const saveBatch = async () => {
    if (!batchForm.name || !selectedDept || batchCodeStatus === 'taken' || batchCodeStatus === 'invalid') return;
    await api(`/institution/departments/${selectedDept.id}/batches`, {
      method: 'POST', body: JSON.stringify(batchForm)
    });
    showToast('Batch created');
    setShowBatchForm(false);
    setBatchForm({ name: '', code: '', year: '' });
    setBatchFromYear(''); setBatchToYear(''); setBatchCodeTouched(false); setBatchCodeStatus(null);
    const d = await api(`/institution/departments/${selectedDept.id}/batches`);
    setBatches(d.batches || []);
  };

  const deleteBatch = async (batchId) => {
    if (!(await confirmDialog({ title: 'Delete batch', message: 'Delete this batch and all its classes?', danger: true, confirmLabel: 'Delete' }))) return;
    await api(`/institution/batches/${batchId}`, { method: 'DELETE' });
    showToast('Batch deleted');
    const d = await api(`/institution/departments/${selectedDept.id}/batches`);
    setBatches(d.batches || []);
  };

  const createDeptAdmin = async () => {
    if (!adminForm.email || !adminForm.password || !adminForm.full_name) return;
    setAdminCreating(true);
    try {
      const r = await api(`/institution/departments/${selectedDept.id}/create-admin`, {
        method: 'POST', body: JSON.stringify(adminForm)
      });
      if (r.user) {
        setDeptAdmin(r.user);
        toast.success('Department admin created');
        setShowAdminPanel(false);
        setAdminForm({ email: '', password: '', full_name: '' });
      } else {
        notifyError(r, 'Failed to create admin');
      }
    } catch (e) { toast.error('Error creating admin'); }
    setAdminCreating(false);
  };

  const updateDeptAdmin = async () => {
    if (!deptAdmin) return;
    setAdminCreating(true);
    try {
      const payload = {};
      if (adminForm.full_name && adminForm.full_name !== deptAdmin.full_name) payload.full_name = adminForm.full_name;
      if (adminForm.email && adminForm.email !== deptAdmin.email) payload.email = adminForm.email;
      if (resetPasswordForm) payload.new_password = resetPasswordForm;

      const r = await api(`/institution/departments/${selectedDept.id}/update-admin`, {
        method: 'PUT', body: JSON.stringify(payload)
      });
      if (r.user || r.updated) {
        if (r.user) setDeptAdmin(r.user);
        toast.success('Admin updated successfully');
        setEditingAdmin(false);
        setResetPasswordForm('');
      } else {
        notifyError(r, 'Update failed');
      }
    } catch (e) { toast.error('Error updating admin'); }
    setAdminCreating(false);
  };

  // Open class management
  const openClass = async (cls) => {
    setSelectedClass(cls);
    setView('class');
    setClassTab('students');
    setClassStudents([]);
    setPendingEnrollments([]);
    loadClassSubs(cls.id);
    setOpening(true);
    try { await Promise.all([loadClassStudents(cls.id), loadPendingEnrollments(cls.id)]); }
    catch (e) { notifyError(e, "Couldn't load students."); }
    setOpening(false);
  };

  const loadClassSubs = async (cid) => {
    try {
      const r = await api(`/classes/${cid}/substitutes`);
      setClassSubs(Array.isArray(r) ? r : []);
    } catch { setClassSubs([]); }
  };

  // Student loaders
  const loadClassStudents = async (classId) => {
    const d = await api(`/institution/classes/${classId}/students`);
    setClassStudents(d.students || []);
  };
  const loadPendingEnrollments = async (classId) => {
    const d = await api(`/institution/classes/${classId}/pending-enrollments`);
    setPendingEnrollments(d.enrollments || []);
  };
  const loadAvailableStudents = async (classId) => {
    const d = await api(`/institution/classes/${classId}/available-students`);
    setAvailableStudents(d.students || []);
  };

  // Helpers
  const copyCode = (code) => { navigator.clipboard.writeText(code); showToast('Class code copied'); };

  // ─── DEPARTMENT CRUD ──────────────────────────────────────────────────────
  // Auto-suggest a code from the name while the user hasn't typed into the
  // code field themselves.
  useEffect(() => {
    if (!showDeptForm || codeTouched) return;
    const name = deptForm.name.trim();
    if (!name) { setDeptForm(p => ({ ...p, code: '' })); setCodeStatus(null); return; }
    const t = setTimeout(async () => {
      try {
        const r = await api(`/institution/departments/suggest-code?name=${encodeURIComponent(name)}`);
        if (r?.code) { setDeptForm(p => ({ ...p, code: r.code })); setCodeStatus('available'); }
      } catch { /* best-effort suggestion */ }
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deptForm.name, codeTouched, showDeptForm]);

  // Once the user edits the code directly, check its availability instead
  // of auto-suggesting over it.
  useEffect(() => {
    if (!showDeptForm || !codeTouched) return;
    const code = deptForm.code.trim();
    if (!code) { setCodeStatus(null); return; }
    if (!CODE_FORMAT_RE.test(code)) { setCodeStatus('invalid'); return; }
    setCodeStatus('checking');
    const t = setTimeout(async () => {
      try {
        const excludeParam = editingDept ? `&exclude_id=${editingDept.id}` : '';
        const r = await api(`/institution/departments/check-code?code=${encodeURIComponent(code)}${excludeParam}`);
        setCodeStatus(r?.available ? 'available' : 'taken');
      } catch { setCodeStatus(null); }
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deptForm.code, codeTouched, showDeptForm, editingDept]);

  const saveDept = async () => {
    if (!deptForm.name || codeStatus === 'taken' || codeStatus === 'invalid') return;
    if (editingDept) {
      await api(`/institution/departments/${editingDept.id}`, { method: 'PUT', body: JSON.stringify(deptForm) });
      showToast('Department updated');
    } else {
      await api('/institution/departments', { method: 'POST', body: JSON.stringify(deptForm) });
      showToast('Department created');
    }
    setShowDeptForm(false); setEditingDept(null); setDeptForm({ name: '', code: '', head_teacher_id: '' });
    setCodeTouched(false); setCodeStatus(null);
    loadDepartments();
  };

  const deleteDept = async (id) => {
    if (!(await confirmDialog({ title: 'Delete department', message: 'Delete this department and all its classes?', danger: true, confirmLabel: 'Delete' }))) return;
    await api(`/institution/departments/${id}`, { method: 'DELETE' });
    showToast('Department deleted'); loadDepartments();
  };

  // ─── CLASS CRUD ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (!showClassForm || classCodeTouched) return;
    (async () => {
      try {
        const r = await api('/institution/classes/suggest-code');
        if (r?.code) { setClassForm(p => ({ ...p, code: r.code })); setClassCodeStatus('available'); }
      } catch { /* best-effort suggestion */ }
    })();
  }, [showClassForm, classCodeTouched]);

  useEffect(() => {
    if (!showClassForm || !classCodeTouched) return;
    const code = classForm.code.trim();
    if (!code) { setClassCodeStatus(null); return; }
    if (!CODE_FORMAT_RE.test(code)) { setClassCodeStatus('invalid'); return; }
    setClassCodeStatus('checking');
    const t = setTimeout(async () => {
      try {
        const r = await api(`/institution/classes/check-code?code=${encodeURIComponent(code)}`);
        setClassCodeStatus(r?.available ? 'available' : 'taken');
      } catch { setClassCodeStatus(null); }
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classForm.code, classCodeTouched, showClassForm]);

  const saveClass = async () => {
    if (!classForm.name || !selectedDept || classCodeStatus === 'taken' || classCodeStatus === 'invalid') return;
    const body = { ...classForm };
    if (selectedBatch) body.batch_id = selectedBatch.id;
    await api(`/institution/departments/${selectedDept.id}/classes`, { method: 'POST', body: JSON.stringify(body) });
    showToast('Class created'); setShowClassForm(false); setClassForm({ name: '', code: '', capacity: 30 });
    setClassCodeTouched(false); setClassCodeStatus(null);
    if (selectedBatch) {
      const d = await api(`/institution/departments/${selectedDept.id}/classes?batch_id=${selectedBatch.id}`);
      setClasses(d.classes || []);
    }
  };

  const deleteClass = async (id) => {
    if (!(await confirmDialog({ title: 'Delete class', message: 'Delete this class and all its data?', danger: true, confirmLabel: 'Delete' }))) return;
    await api(`/institution/classes/${id}`, { method: 'DELETE' });
    showToast('Class deleted');
    if (selectedBatch) {
      const d = await api(`/institution/departments/${selectedDept.id}/classes?batch_id=${selectedBatch.id}`);
      setClasses(d.classes || []);
    }
  };

  // ─── ENROLLMENT ACTIONS ───────────────────────────────────────────────────
  const addStudentsByEmail = async () => {
    if (!emailInput.trim()) return;
    setEnrollLoading(true);
    const emails = emailInput.split(/[\n,;]+/).map(e => e.trim()).filter(Boolean);
    let r;
    try { r = await api(`/institution/classes/${selectedClass.id}/add-students`, { method: 'POST', body: JSON.stringify({ emails }) }); }
    catch (e) { notifyError(e, "Couldn't add the students."); return; }
    finally { setEnrollLoading(false); }
    const parts = [];
    if (r.added?.length) parts.push(`${r.added.length} added`);
    if (r.not_found?.length) parts.push(`${r.not_found.length} not found`);
    if (r.already_enrolled?.length) parts.push(`${r.already_enrolled.length} already enrolled`);
    showToast(parts.join(', '));
    setEmailInput(''); setAddMode(null);
    loadClassStudents(selectedClass.id);
  };

  const addStudentsByCSV = async () => {
    if (!csvInput.trim()) return;
    setEnrollLoading(true);
    let r;
    try { r = await api(`/institution/classes/${selectedClass.id}/add-students-csv`, { method: 'POST', body: JSON.stringify({ csv_text: csvInput }) }); }
    catch (e) { notifyError(e, "Couldn't add the students."); return; }
    finally { setEnrollLoading(false); }
    const parts = [];
    if (r.added?.length) parts.push(`${r.added.length} added`);
    if (r.not_found?.length) parts.push(`${r.not_found.length} not found`);
    if (r.already_enrolled?.length) parts.push(`${r.already_enrolled.length} already enrolled`);
    showToast(parts.join(', '));
    setCsvInput(''); setAddMode(null);
    loadClassStudents(selectedClass.id);
  };

  const addSelectedStudents = async () => {
    if (!selectedAvailable.length) return;
    setEnrollLoading(true);
    const emails = selectedAvailable.map(s => s.email);
    try { await api(`/institution/classes/${selectedClass.id}/add-students`, { method: 'POST', body: JSON.stringify({ emails }) }); }
    catch (e) { notifyError(e, "Couldn't add the students."); return; }
    finally { setEnrollLoading(false); }
    showToast(`${selectedAvailable.length} students added`);
    setSelectedAvailable([]); setAddMode(null);
    loadClassStudents(selectedClass.id);
    loadAvailableStudents(selectedClass.id);
  };

  const handleEnrollmentAction = async (enrollmentId, action) => {
    await api(`/institution/classes/${selectedClass.id}/enrollment-action`, { method: 'POST', body: JSON.stringify({ enrollment_id: enrollmentId, action }) });
    showToast(`Enrollment ${action}d`);
    loadPendingEnrollments(selectedClass.id);
    if (action === 'approve') loadClassStudents(selectedClass.id);
  };

  const removeStudent = async (studentId) => {
    if (!(await confirmDialog({ title: 'Remove student', message: 'Remove this student from the class?', danger: true, confirmLabel: 'Remove' }))) return;
    await api(`/institution/classes/${selectedClass.id}/remove-student`, { method: 'POST', body: JSON.stringify({ student_id: studentId }) });
    showToast('Student removed');
    loadClassStudents(selectedClass.id);
  };

  // ─── BREADCRUMB ───────────────────────────────────────────────────────────
  const Breadcrumb = () => (
    <div className="flex items-center gap-1.5 mb-6" data-testid="breadcrumb-nav">
      <button onClick={() => { setView('list'); setSelectedDept(null); setSelectedBatch(null); setSelectedClass(null); }}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors duration-150 hover:bg-gray-100"
        style={{ color: A }} data-testid="breadcrumb-departments">
        <Building2 className="w-3.5 h-3.5" /> Departments
      </button>
      {selectedDept && (<>
        <ChevronRight className="w-3.5 h-3.5 text-gray-400" />
        <button onClick={() => { setView('detail'); setSelectedBatch(null); setSelectedClass(null); }}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors duration-150 hover:bg-gray-100"
          style={{ color: A }} data-testid="breadcrumb-dept-name">
          {selectedDept.name}
        </button>
      </>)}
      {selectedBatch && (<>
        <ChevronRight className="w-3.5 h-3.5 text-gray-400" />
        <button onClick={() => { setView('batch'); setSelectedClass(null); }}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors duration-150 hover:bg-gray-100"
          style={{ color: A }} data-testid="breadcrumb-batch-name">
          {selectedBatch.name}
        </button>
      </>)}
      {selectedClass && (<>
        <ChevronRight className="w-3.5 h-3.5 text-gray-400" />
        <span className="px-3 py-1.5 text-sm font-semibold text-gray-900" data-testid="breadcrumb-class-name">{selectedClass.name}</span>
      </>)}
    </div>
  );

  const StatCard = ({ icon: Icon, label, value, color }) => (
    <div className="bg-white rounded-2xl border border-gray-100 p-5 flex items-center gap-4 transition-shadow duration-200 hover:shadow-md">
      <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${color}14` }}>
        <Icon className="w-5 h-5" style={{ color }} />
      </div>
      <div>
        <p className="text-2xl font-semibold text-gray-900 tabular-nums leading-none">{value}</p>
        <p className="text-xs text-gray-400 mt-1 font-medium">{label}</p>
      </div>
    </div>
  );

  // ═══════════════════════════════════════════════════════════════════════════
  // LIST VIEW — Departments
  // ═══════════════════════════════════════════════════════════════════════════
  if (view === 'list') return (
    <div className="space-y-6" data-testid="departments-list">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-2xl font-semibold text-gray-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Departments & classes</h3>
          <p className="text-sm text-gray-500 mt-2 leading-relaxed">Organize your institution into departments, manage classes, enroll students and publish materials</p>
        </div>
        <Button onClick={() => { setShowDeptForm(true); setEditingDept(null); setDeptForm({ name: '', code: '', head_teacher_id: '' }); setCodeTouched(false); setCodeStatus(null); }}
          className="h-10 px-5 rounded-xl text-white text-sm font-semibold flex-shrink-0 active:scale-[0.98] transition-transform duration-100"
          style={{ background: A }} data-testid="create-dept-btn">
          <Plus className="w-4 h-4 mr-1.5" /> New department
        </Button>
      </div>

      {departments.length > 0 && (
        <div className="grid grid-cols-3 gap-4" data-testid="dept-stats-bar">
          <StatCard icon={Building2} label="Departments" value={totals.depts} color={A} />
          <StatCard icon={Layers} label="Total classes" value={totals.classes} color="#1A7F37" />
          <StatCard icon={GraduationCap} label="Students enrolled" value={totals.students} color="#1A7F37" />
        </div>
      )}

      {departments.length > 0 && (
        <Card className="!p-3" data-testid="dept-search-filters">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search departments by name or code..." className="h-10 pl-10 rounded-xl text-sm border-gray-200 bg-white" data-testid="dept-search-input" />
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Filter className="w-3.5 h-3.5 text-gray-400" />
              <select value={deptFilter} onChange={e => setDeptFilter(e.target.value)} className="h-10 text-xs rounded-xl border border-gray-200 px-3 bg-white focus:outline-none" data-testid="dept-head-filter">
                <option value="all">All departments</option>
                <option value="has_head">Has head teacher</option>
                <option value="no_head">No head teacher</option>
              </select>
              <select value={deptSortBy} onChange={e => setDeptSortBy(e.target.value)} className="h-10 text-xs rounded-xl border border-gray-200 px-3 bg-white focus:outline-none" data-testid="dept-sort">
                <option value="name">Sort: Name</option>
                <option value="classes">Sort: Most Classes</option>
                <option value="students">Sort: Most Students</option>
              </select>
            </div>
            <span className="text-xs text-gray-400 ml-auto">{filteredDepts.length} of {departments.length}</span>
          </div>
        </Card>
      )}

      {showDeptForm && (
        <div className="bg-white rounded-2xl border-2 border-dashed p-6 space-y-5" style={{ borderColor: `${A}40` }} data-testid="dept-form">
          <div className="flex items-center justify-between">
            <h4 className="text-lg font-semibold text-gray-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{editingDept ? 'Edit Department' : 'Create New Department'}</h4>
            <button onClick={() => { setShowDeptForm(false); setEditingDept(null); setCodeTouched(false); setCodeStatus(null); }} className="p-1.5 rounded-lg hover:bg-gray-100"><X className="w-4 h-4 text-gray-400" /></button>
          </div>
          <div className="grid sm:grid-cols-3 gap-5">
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1.5">Department name</label>
              <Input value={deptForm.name} onChange={e => setDeptForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Science" className="h-11 rounded-xl text-sm" data-testid="dept-name-input" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1.5">Short code</label>
              <Input
                value={deptForm.code}
                onChange={e => { setCodeTouched(true); setDeptForm(p => ({ ...p, code: e.target.value.toUpperCase() })); }}
                placeholder="auto-suggested from name"
                maxLength={7}
                className="h-11 rounded-xl text-sm font-mono"
                data-testid="dept-code-input"
              />
              {codeStatus === 'checking' && <p className="text-xs text-gray-400 mt-1">Checking…</p>}
              {codeStatus === 'available' && deptForm.code && <p className="text-xs text-emerald-600 mt-1">Available</p>}
              {codeStatus === 'taken' && <p className="text-xs text-red-500 mt-1" data-testid="dept-code-taken">Already in use</p>}
              {codeStatus === 'invalid' && <p className="text-xs text-red-500 mt-1" data-testid="dept-code-invalid">Wrong format</p>}
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1.5">Head teacher</label>
              <select value={deptForm.head_teacher_id} onChange={e => setDeptForm(p => ({ ...p, head_teacher_id: e.target.value }))} className="w-full h-11 rounded-xl border border-gray-200 text-sm px-3 bg-white focus:outline-none focus:ring-2 focus:ring-offset-1" data-testid="dept-head-select">
                <option value="">No head teacher</option>
                {teachers.map(t => <option key={t.id} value={t.id}>{t.full_name}</option>)}
              </select>
              {deptForm.head_teacher_id && (
                <p className="text-xs text-gray-500 mt-1.5" data-testid="dept-head-login-hint">
                  The head teacher can also sign in to the institute login with their teacher email and password to manage this department.
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3 pt-2 border-t border-gray-100">
            <Button onClick={saveDept} disabled={codeStatus === 'taken' || codeStatus === 'invalid'} className="h-10 px-6 rounded-xl text-white text-sm font-semibold disabled:opacity-50" style={{ background: A }} data-testid="save-dept-btn">
              <Save className="w-4 h-4 mr-1.5" /> {editingDept ? 'Update' : 'Create Department'}
            </Button>
            <Button variant="ghost" onClick={() => { setShowDeptForm(false); setEditingDept(null); setCodeTouched(false); setCodeStatus(null); }} className="h-10 px-4 rounded-xl text-sm text-gray-500">Cancel</Button>
          </div>
        </div>
      )}

      {loading ? (
        <DeptCardsSkeleton />
      ) : filteredDepts.length === 0 && departments.length === 0 ? (
        <div className="bg-white rounded-2xl border-2 border-dashed border-gray-200 text-center py-20 px-8" data-testid="empty-departments">
          <div className="w-16 h-16 rounded-2xl mx-auto mb-5 flex items-center justify-center" style={{ background: `${A}10` }}>
            <Building2 className="w-8 h-8" style={{ color: A }} />
          </div>
          <h4 className="text-lg font-semibold text-gray-800" style={{ fontFamily: 'Manrope, sans-serif' }}>No departments yet</h4>
          <p className="text-sm text-gray-400 mt-2 max-w-sm mx-auto">Create your first department to start organizing classes.</p>
          <Button onClick={() => { setShowDeptForm(true); setEditingDept(null); setDeptForm({ name: '', code: '', head_teacher_id: '' }); setCodeTouched(false); setCodeStatus(null); }}
            className="h-10 px-5 rounded-xl text-white text-sm font-semibold mt-6" style={{ background: A }}>
            <Plus className="w-4 h-4 mr-1.5" /> Create first department
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
          {filteredDepts.map((d) => (
            <div key={d.id} className="bg-white rounded-xl border border-gray-200 p-4 group relative transition hover:shadow-md cursor-pointer" data-testid={`dept-card-${d.id}`}>
              <div className="absolute top-0 left-0 w-full h-1 rounded-t-xl" style={{ background: A }} />
              <button onClick={(e) => { e.stopPropagation(); deleteDept(d.id); }} className="absolute top-2 right-2 p-1 rounded-lg opacity-0 group-hover:opacity-100 text-gray-300 hover:text-red-500 hover:bg-red-50 transition"><Trash2 className="w-3 h-3" /></button>
              <div className="p-0" onClick={() => openDept(d)}>
                {d.sticker_url
                  ? <img src={d.sticker_url} alt="" className="w-8 h-8 rounded-lg object-cover mb-2.5" />
                  : <div className="w-8 h-8 rounded-lg flex items-center justify-center mb-2.5" style={{ background: `${A}12` }}><Building2 className="w-4 h-4" style={{ color: A }} /></div>}
                <h4 className="text-sm font-semibold text-gray-900 truncate leading-tight">{d.name}</h4>
                <p className="text-xs text-gray-400 font-mono mt-0.5">{d.code}</p>
                <div className="flex items-center gap-2 mt-2">
                  <span className="text-xs font-semibold" style={{ color: A }}>{d.class_count || 0} {d.class_count === 1 ? 'class' : 'classes'}</span>
                  <span className="text-xs text-gray-400">{d.student_count || 0} {d.student_count === 1 ? 'student' : 'students'}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // ═══════════════════════════════════════════════════════════════════════════
  // DETAIL VIEW — Batches inside a Department
  // ═══════════════════════════════════════════════════════════════════════════
  if (view === 'detail' && selectedDept) {
    const batchSearch = searchQuery.toLowerCase();
    const filteredBatches = batches.filter(b => b.name.toLowerCase().includes(batchSearch) || b.code.toLowerCase().includes(batchSearch));
    return (
    <div className="space-y-6" data-testid="dept-detail">
      <Breadcrumb />
      <div className="bg-white rounded-2xl border border-gray-100 p-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-4">
            {selectedDept.sticker_url
              ? <img src={selectedDept.sticker_url} alt="" className="w-14 h-14 rounded-2xl object-cover flex-shrink-0 shadow" />
              : <div className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: `${A}12` }}><Building2 className="w-6 h-6" style={{ color: A }} /></div>}
            <div>
              <h3 className="text-xl font-semibold text-gray-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{selectedDept.name}</h3>
              <div className="flex items-center gap-3 mt-1.5 text-sm text-gray-400 flex-wrap">
                <span className="font-mono text-xs bg-gray-50 px-2 py-0.5 rounded-md">{selectedDept.code}</span>
                <span>{batches.length} batch{batches.length !== 1 ? 'es' : ''}</span>
                {deptAdmin && (<>
                  <span className="w-px h-3.5 bg-gray-200" />
                  <span className="flex items-center gap-1.5 text-xs text-gray-500"><ShieldCheck className="w-3.5 h-3.5 text-green-500" /><span className="font-medium text-gray-700">{deptAdmin.full_name}</span><span className="text-gray-400">({deptAdmin.email})</span></span>
                </>)}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button onClick={() => { setShowAdminPanel(true); if (deptAdmin) { setAdminForm({ full_name: deptAdmin.full_name || '', email: deptAdmin.email || '', password: '' }); } }} variant="outline" className="h-9 px-4 rounded-xl text-xs font-semibold border-violet-200 text-violet-600 hover:bg-violet-50" data-testid="manage-dept-admin-btn"><ShieldCheck className="w-3.5 h-3.5 mr-1" /> {deptAdmin ? 'Manage Access' : 'Create Login'}</Button>
            <Button onClick={() => {
              const cy = new Date().getFullYear();
              setShowBatchForm(true);
              setBatchForm({ name: '', code: '', year: `${cy}-${String(cy + 1).slice(-2)}` });
              setBatchFromYear(String(cy)); setBatchToYear(String(cy + 1));
              setBatchCodeTouched(false); setBatchCodeStatus(null);
            }} className="h-9 px-4 rounded-xl text-white text-sm font-semibold" style={{ background: A }} data-testid="create-batch-btn"><Plus className="w-4 h-4 mr-1.5" /> New batch</Button>
          </div>
        </div>
      </div>

      {/* ─── Department Admin Panel (Modal) ─── */}
      {showAdminPanel && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center" style={{ pointerEvents: 'auto' }}>
          <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={() => setShowAdminPanel(false)} />
          <div className="relative bg-white rounded-2xl w-[520px] shadow-2xl border border-gray-100 overflow-hidden" style={{ pointerEvents: 'auto' }} data-testid="admin-panel">
            {/* Header */}
            <div className="p-5 pb-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: '#7C3AED14' }}><ShieldCheck className="w-5 h-5 text-violet-600" /></div>
                  <div><h3 className="text-base font-semibold text-gray-900">Department access</h3><p className="text-xs text-gray-500">{selectedDept.name} · {selectedDept.code}</p></div>
                </div>
                <button onClick={() => setShowAdminPanel(false)} className="p-2 rounded-lg hover:bg-gray-100"><X className="w-4 h-4 text-gray-400" /></button>
              </div>
            </div>

            <div className="p-5 space-y-4">
              {deptAdmin && !editingAdmin ? (
                <>
                  {/* Current Admin Info */}
                  <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                    <p className="text-xs font-semibold text-gray-400">Current admin</p>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold text-white bg-violet-500">{(deptAdmin.full_name || '?')[0]}</div>
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-gray-900">{deptAdmin.full_name}</p>
                        <p className="text-xs text-gray-500">{deptAdmin.email}</p>
                      </div>
                      <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-green-50 text-green-600">{deptAdmin.status || 'Active'}</span>
                    </div>
                  </div>

                  {/* Security Status */}
                  <div className="bg-gray-50 rounded-xl p-4">
                    <p className="text-xs font-semibold text-gray-400 mb-2">Security</p>
                    <div className="flex items-center gap-2"><Lock className="w-3.5 h-3.5 text-gray-400" /><span className="text-xs text-gray-700">Signs in on MyDeskey (Institution tab). 2FA follows Settings → Security → Institution Admins.</span></div>
                    {deptAdmin.created_at && <p className="text-xs text-gray-400 mt-2">Created: {new Date(deptAdmin.created_at).toLocaleDateString()}</p>}
                  </div>

                  {/* Actions — a new password is set through Edit Details */}
                  <div className="flex gap-2">
                    <Button onClick={() => { setEditingAdmin(true); setAdminForm({ full_name: deptAdmin.full_name, email: deptAdmin.email, password: '' }); }} variant="outline" className="flex-1 h-9 rounded-xl text-xs font-semibold border-gray-200 text-gray-700" data-testid="edit-admin-btn"><Edit3 className="w-3.5 h-3.5 mr-1" /> Edit Details / Reset Password</Button>
                  </div>
                  <p className="text-xs text-gray-400 text-center">Institution admins have full override access to all department data</p>
                </>
              ) : deptAdmin && editingAdmin ? (
                <>
                  {/* Edit Admin Form */}
                  <div className="space-y-3">
                    <div><label className="text-xs font-semibold text-gray-400 block mb-1">Full name</label><Input value={adminForm.full_name} onChange={e => setAdminForm(p => ({ ...p, full_name: e.target.value }))} className="h-10 rounded-xl text-sm" data-testid="edit-admin-name" /></div>
                    <div><label className="text-xs font-semibold text-gray-400 block mb-1">Email</label><Input type="email" value={adminForm.email} onChange={e => setAdminForm(p => ({ ...p, email: e.target.value }))} className="h-10 rounded-xl text-sm" data-testid="edit-admin-email" /></div>
                    <div><label className="text-xs font-semibold text-gray-400 block mb-1">New Password (min 8 characters, leave blank to keep current)</label><Input type="password" value={resetPasswordForm} onChange={e => setResetPasswordForm(e.target.value)} placeholder="Enter new password" className="h-10 rounded-xl text-sm" data-testid="edit-admin-password" /></div>

                    {/* 2FA confirm - institution admin verifies with own password */}
                    <div><label className="text-xs font-semibold text-gray-400 block mb-1">Your Password (to confirm changes)</label><Input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Enter your institution admin password" className="h-10 rounded-xl text-sm" data-testid="confirm-inst-password" /></div>
                  </div>

                  <div className="flex gap-2 pt-2">
                    <Button onClick={async () => {
                      if (!confirmPassword) { toast.error('Enter your password to confirm'); return; }
                      const authRes = await verifyMyPassword(confirmPassword);
                      if (!authRes.ok) { toast.error('Invalid password — 2FA failed'); return; }
                      await updateDeptAdmin();
                      setConfirmPassword('');
                    }} disabled={adminCreating} className="flex-1 h-10 rounded-xl text-white text-sm font-semibold bg-violet-600 hover:bg-violet-700" data-testid="save-admin-btn">{adminCreating ? 'Saving...' : 'Save Changes'}</Button>
                    <Button variant="ghost" onClick={() => { setEditingAdmin(false); setResetPasswordForm(''); setConfirmPassword(''); }} className="h-10 px-4 rounded-xl text-sm text-gray-500">Cancel</Button>
                  </div>
                </>
              ) : (
                <>
                  {/* Create Admin Form */}
                  <p className="text-sm text-gray-500">Set up login credentials for the department admin.</p>
                  <div className="space-y-3">
                    <div><label className="text-xs font-semibold text-gray-400 block mb-1">Full Name *</label><Input value={adminForm.full_name} onChange={e => setAdminForm(p => ({ ...p, full_name: e.target.value }))} placeholder="e.g. Dr. Sarah" className="h-10 rounded-xl text-sm" data-testid="dept-admin-name" /></div>
                    <div><label className="text-xs font-semibold text-gray-400 block mb-1">Email *</label><Input type="email" value={adminForm.email} onChange={e => setAdminForm(p => ({ ...p, email: e.target.value }))} placeholder="e.g. head@school.edu" className="h-10 rounded-xl text-sm" data-testid="dept-admin-email" /></div>
                    <div><label className="text-xs font-semibold text-gray-400 block mb-1">Password * (min 8 characters)</label><Input type="password" value={adminForm.password} onChange={e => setAdminForm(p => ({ ...p, password: e.target.value }))} placeholder="Set initial password" className="h-10 rounded-xl text-sm" data-testid="dept-admin-password" /></div>
                    <p className="text-xs text-gray-400">They sign in on MyDeskey with the Institution tab.</p>
                  </div>
                  <Button onClick={createDeptAdmin} disabled={adminCreating || !adminForm.email || adminForm.password.length < 8 || !adminForm.full_name} className="w-full h-10 rounded-xl text-white text-sm font-semibold bg-violet-600 hover:bg-violet-700 disabled:opacity-50" data-testid="submit-dept-admin-btn">{adminCreating ? 'Creating...' : 'Create Department Admin'}</Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {showBatchForm && (
        <div className="bg-white rounded-2xl border-2 border-dashed p-6 space-y-5" style={{ borderColor: `${A}40` }} data-testid="batch-form">
          <div className="flex items-center justify-between">
            <h4 className="text-lg font-semibold text-gray-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Create new batch</h4>
            <button onClick={() => { setShowBatchForm(false); setBatchFromYear(''); setBatchToYear(''); setBatchCodeTouched(false); setBatchCodeStatus(null); }} className="p-1.5 rounded-lg hover:bg-gray-100"><X className="w-4 h-4 text-gray-400" /></button>
          </div>
          <div className="grid sm:grid-cols-3 gap-5">
            <div><label className="block text-xs font-semibold text-gray-500 mb-1.5">Batch name</label><Input value={batchForm.name} onChange={e => setBatchForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Batch 2025-26" className="h-11 rounded-xl text-sm" data-testid="batch-name-input" /></div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1.5">Academic year</label>
              <div className="flex items-center gap-2">
                <select
                  value={batchFromYear}
                  onChange={e => {
                    const from = e.target.value;
                    setBatchFromYear(from);
                    if (Number(batchToYear) <= Number(from)) setBatchToYear(String(Number(from) + 1));
                  }}
                  className="w-full h-11 rounded-xl border border-gray-200 text-sm px-2 bg-white focus:outline-none focus:ring-2 focus:ring-offset-1"
                  data-testid="batch-from-year-select">
                  {Array.from({ length: 21 }, (_, i) => new Date().getFullYear() - 10 + i).map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
                <span className="text-gray-400 text-sm">to</span>
                <select
                  value={batchToYear}
                  onChange={e => setBatchToYear(e.target.value)}
                  className="w-full h-11 rounded-xl border border-gray-200 text-sm px-2 bg-white focus:outline-none focus:ring-2 focus:ring-offset-1"
                  data-testid="batch-to-year-select">
                  {Array.from({ length: 10 }, (_, i) => (Number(batchFromYear) || new Date().getFullYear()) + 1 + i).map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1.5">Batch Code (optional)</label>
              <Input
                value={batchForm.code}
                onChange={e => { setBatchCodeTouched(true); setBatchForm(p => ({ ...p, code: e.target.value.toUpperCase() })); }}
                placeholder="auto-generated"
                maxLength={7}
                className="h-11 rounded-xl text-sm font-mono"
                data-testid="batch-code-input"
              />
              {batchCodeStatus === 'checking' && <p className="text-xs text-gray-400 mt-1">Checking…</p>}
              {batchCodeStatus === 'available' && batchForm.code && <p className="text-xs text-emerald-600 mt-1">Available</p>}
              {batchCodeStatus === 'taken' && <p className="text-xs text-red-500 mt-1" data-testid="batch-code-taken">Already in use</p>}
              {batchCodeStatus === 'invalid' && <p className="text-xs text-red-500 mt-1" data-testid="batch-code-invalid">Wrong format</p>}
            </div>
          </div>
          <div className="flex items-center gap-3 pt-2 border-t border-gray-100">
            <Button onClick={saveBatch} disabled={batchCodeStatus === 'taken' || batchCodeStatus === 'invalid'} className="h-10 px-6 rounded-xl text-white text-sm font-semibold disabled:opacity-50" style={{ background: A }} data-testid="save-batch-btn"><Save className="w-4 h-4 mr-1.5" /> Create batch</Button>
            <Button variant="ghost" onClick={() => { setShowBatchForm(false); setBatchFromYear(''); setBatchToYear(''); setBatchCodeTouched(false); setBatchCodeStatus(null); }} className="h-10 px-4 rounded-xl text-sm text-gray-500">Cancel</Button>
          </div>
        </div>
      )}

      {batches.length > 1 && (
        <Card className="!p-3"><div className="flex items-center gap-3">
          <div className="relative flex-1 min-w-[180px]"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" /><Input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search batches..." className="pl-9 h-9 text-sm rounded-lg" /></div>
          <span className="text-xs text-gray-400">{filteredBatches.length} of {batches.length}</span>
        </div></Card>
      )}

      {opening ? <BigCardsSkeleton label="Loading batches…" testId="batches-loading" topBar /> : filteredBatches.length === 0 && batches.length === 0 ? (
        <div className="bg-white rounded-2xl border-2 border-dashed border-gray-200 text-center py-20 px-8" data-testid="empty-batches">
          <div className="w-16 h-16 rounded-2xl mx-auto mb-5 flex items-center justify-center" style={{ background: `${A}10` }}><Calendar className="w-8 h-8" style={{ color: A }} /></div>
          <h4 className="text-lg font-semibold text-gray-800" style={{ fontFamily: 'Manrope, sans-serif' }}>No batches yet</h4>
          <p className="text-sm text-gray-400 mt-2 max-w-sm mx-auto">Create your first batch to organize classes by academic year.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {filteredBatches.map((b) => (
            <div key={b.id} className="bg-white rounded-2xl border border-gray-100 overflow-hidden group relative transition duration-200 hover:shadow-lg cursor-pointer" data-testid={`batch-card-${b.id}`}>
              <div className="h-1.5 w-full" style={{ background: b.status === 'active' ? A : '#94a3b8' }} />
              <div className="absolute top-5 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10">
                <button onClick={(e) => { e.stopPropagation(); deleteBatch(b.id); }} className="p-2 rounded-xl bg-white shadow border border-gray-100 hover:bg-red-50" data-testid={`delete-batch-${b.id}`}><Trash2 className="w-3.5 h-3.5 text-red-400" /></button>
              </div>
              <div className="p-6" onClick={() => openBatch(b)}>
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: `${A}12` }}><Calendar className="w-5 h-5" style={{ color: A }} /></div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold px-2.5 py-1 rounded-lg bg-gray-50 text-gray-500 font-mono">{b.code}</span>
                    <span className={`text-xs px-2 py-1 rounded-full font-semibold ${b.status === 'active' ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-50 text-gray-400'}`}>{b.status === 'active' ? 'Active' : 'Archived'}</span>
                  </div>
                </div>
                <h4 className="text-base font-semibold text-gray-900">{b.name}</h4>
                {b.year && <p className="text-xs text-gray-400 mt-1">Academic Year: {b.year}</p>}
                <div className="flex items-center gap-2.5 mt-5 pt-4 border-t border-gray-50 flex-wrap">
                  <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-50 text-xs font-medium text-gray-600"><Layers className="w-3.5 h-3.5 text-gray-400" />{b.class_count || 0} classes</span>
                  <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-50 text-xs font-medium text-gray-600"><GraduationCap className="w-3.5 h-3.5 text-gray-400" />{b.student_count || 0} {b.student_count === 1 ? 'student' : 'students'}</span>
                </div>
                <div className="flex items-center gap-1 text-xs font-medium mt-4 opacity-0 group-hover:opacity-100 transition-opacity duration-200" style={{ color: A }}>View classes <ChevronRight className="w-3.5 h-3.5" /></div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // BATCH VIEW — Classes inside a Batch
  // ═══════════════════════════════════════════════════════════════════════════
  if (view === 'batch' && selectedBatch) return (
    <div className="space-y-6" data-testid="batch-detail">
      <Breadcrumb />
      <div className="bg-white rounded-2xl border border-gray-100 p-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: `${A}12` }}><Calendar className="w-6 h-6" style={{ color: A }} /></div>
            <div>
              <h3 className="text-xl font-semibold text-gray-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{selectedBatch.name}</h3>
              <div className="flex items-center gap-3 mt-1.5 text-sm text-gray-400">
                <span className="font-mono text-xs bg-gray-50 px-2 py-0.5 rounded-md">{selectedBatch.code}</span>
                {selectedBatch.year && <span>Year: {selectedBatch.year}</span>}
                <span>{classes.length} classes</span>
              </div>
            </div>
          </div>
          <Button onClick={() => { setShowClassForm(true); setClassForm({ name: '', code: '', capacity: 30 }); setClassCodeTouched(false); setClassCodeStatus(null); }}
            className="h-10 px-5 rounded-xl text-white text-sm font-semibold flex-shrink-0" style={{ background: A }} data-testid="create-class-btn"><Plus className="w-4 h-4 mr-1.5" /> New class</Button>
        </div>
      </div>

      {showClassForm && (
        <div className="bg-white rounded-2xl border-2 border-dashed p-6 space-y-5" style={{ borderColor: `${A}40` }} data-testid="class-form">
          <div className="flex items-center justify-between">
            <h4 className="text-lg font-semibold text-gray-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Add Class to {selectedBatch.name}</h4>
            <button onClick={() => { setShowClassForm(false); setClassCodeTouched(false); setClassCodeStatus(null); }} className="p-1.5 rounded-lg hover:bg-gray-100"><X className="w-4 h-4 text-gray-400" /></button>
          </div>
          <div className="grid sm:grid-cols-3 gap-5">
            <div><label className="block text-xs font-semibold text-gray-500 mb-1.5">Class name</label><Input value={classForm.name} onChange={e => setClassForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Grade 10A" className="h-11 rounded-xl text-sm" data-testid="class-name-input" /></div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1.5">Class code</label>
              <Input
                value={classForm.code}
                onChange={e => { setClassCodeTouched(true); setClassForm(p => ({ ...p, code: e.target.value.toUpperCase() })); }}
                placeholder="auto-generated"
                maxLength={7}
                className="h-11 rounded-xl text-sm font-mono"
                data-testid="class-code-input"
              />
              {classCodeStatus === 'checking' && <p className="text-xs text-gray-400 mt-1">Checking…</p>}
              {classCodeStatus === 'available' && classForm.code && <p className="text-xs text-emerald-600 mt-1">Available</p>}
              {classCodeStatus === 'taken' && <p className="text-xs text-red-500 mt-1" data-testid="class-code-taken">Already in use</p>}
              {classCodeStatus === 'invalid' && <p className="text-xs text-red-500 mt-1" data-testid="class-code-invalid">Wrong format</p>}
            </div>
            <div><label className="block text-xs font-semibold text-gray-500 mb-1.5">Capacity</label><Input type="number" value={classForm.capacity} onChange={e => setClassForm(p => ({ ...p, capacity: parseInt(e.target.value) || 30 }))} className="h-11 rounded-xl text-sm" /></div>
          </div>
          <div className="flex items-center gap-3 pt-2 border-t border-gray-100">
            <Button onClick={saveClass} disabled={classCodeStatus === 'taken' || classCodeStatus === 'invalid'} className="h-10 px-6 rounded-xl text-white text-sm font-semibold disabled:opacity-50" style={{ background: A }} data-testid="save-class-btn"><Save className="w-4 h-4 mr-1.5" /> Create class</Button>
            <Button variant="ghost" onClick={() => { setShowClassForm(false); setClassCodeTouched(false); setClassCodeStatus(null); }} className="h-10 px-4 rounded-xl text-sm text-gray-500">Cancel</Button>
          </div>
        </div>
      )}

      {classes.length > 0 && (
        <Card className="!p-3" data-testid="class-search-filters"><div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[180px]"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" /><Input value={classSearch} onChange={e => setClassSearch(e.target.value)} placeholder="Search classes..." className="pl-9 h-9 text-sm rounded-lg" data-testid="class-search-input" /></div>
          <div className="flex items-center gap-2"><ArrowUpDown className="w-3.5 h-3.5 text-gray-400" /><select value={classSortBy} onChange={e => setClassSortBy(e.target.value)} className="h-9 text-xs rounded-lg border border-gray-200 px-3 bg-white" data-testid="class-sort"><option value="name">Sort: Name</option><option value="students">Sort: Most Students</option><option value="pending">Sort: Most Pending</option></select></div>
          <span className="text-xs text-gray-400 ml-auto">{filteredClasses.length} of {classes.length}</span>
        </div></Card>
      )}

      {opening ? <BigCardsSkeleton label="Loading classes…" testId="classes-loading" /> : classes.length === 0 ? (
        <div className="bg-white rounded-2xl border-2 border-dashed border-gray-200 text-center py-20 px-8" data-testid="empty-classes">
          <div className="w-16 h-16 rounded-2xl mx-auto mb-5 flex items-center justify-center bg-blue-50"><BookOpen className="w-8 h-8 text-blue-500" /></div>
          <h4 className="text-lg font-semibold text-gray-800" style={{ fontFamily: 'Manrope, sans-serif' }}>No classes in {selectedBatch.name}</h4>
          <p className="text-sm text-gray-400 mt-2 max-w-sm mx-auto">Add classes to start enrolling students.</p>
          <Button onClick={() => { setShowClassForm(true); setClassForm({ name: '', code: '', capacity: 30 }); setClassCodeTouched(false); setClassCodeStatus(null); }} className="h-10 px-5 rounded-xl text-white text-sm font-semibold mt-6" style={{ background: A }}><Plus className="w-4 h-4 mr-1.5" /> Add first class</Button>
        </div>
      ) : filteredClasses.length === 0 ? (
        <div className="bg-white rounded-2xl border-2 border-dashed border-gray-200 text-center py-16 px-8"><BookOpen className="w-12 h-12 text-gray-200 mx-auto mb-3" /><p className="text-sm text-gray-400">No classes match your search</p></div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {filteredClasses.map((c) => (
            <div key={c.id} className="bg-white rounded-2xl border border-gray-100 overflow-hidden group relative transition duration-200 hover:shadow-lg cursor-pointer" data-testid={`class-card-${c.id}`}>
              <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10">
                <button onClick={(e) => { e.stopPropagation(); deleteClass(c.id); }} className="p-2 rounded-xl bg-white shadow border border-gray-100 hover:bg-red-50" data-testid={`delete-class-${c.id}`}><Trash2 className="w-3.5 h-3.5 text-red-400" /></button>
              </div>
              <div className="p-6" onClick={() => openClass(c)}>
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-blue-50"><BookOpen className="w-5 h-5 text-blue-500" /></div>
                  <button onClick={(e) => { e.stopPropagation(); copyCode(c.class_code); }} className="flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-lg bg-amber-50 text-amber-700 font-mono hover:bg-amber-100 transition-colors" data-testid={`copy-code-${c.id}`}><Hash className="w-3 h-3" />{c.class_code || '—'} <ClipboardCopy className="w-2.5 h-2.5 ml-0.5" /></button>
                </div>
                <h4 className="text-base font-semibold text-gray-900">{c.name}</h4>
                <p className="text-xs text-gray-400 mt-1 font-mono">{c.code}</p>
                <div className="flex items-center gap-2.5 mt-5 pt-4 border-t border-gray-50 flex-wrap">
                  <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-green-50 text-xs font-medium text-green-700"><GraduationCap className="w-3 h-3" />{c.student_count || 0} enrolled</span>
                  {(c.pending_count || 0) > 0 && (<span className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-amber-50 text-xs font-medium text-amber-700"><AlertCircle className="w-3 h-3" />{c.pending_count} pending</span>)}
                </div>
                <div className="flex items-center gap-1 text-xs font-medium mt-4 opacity-0 group-hover:opacity-100 transition-opacity duration-200" style={{ color: A }}>Manage class <ChevronRight className="w-3.5 h-3.5" /></div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // ═══════════════════════════════════════════════════════════════════════════
  // CLASS MANAGEMENT VIEW — Tabs: Students, Schedule, Materials
  // ═══════════════════════════════════════════════════════════════════════════
  if (view === 'class' && selectedClass) {
    const CLASS_TABS = [
      { id: 'students', label: 'Enrolled students', icon: GraduationCap, count: classStudents.length },
      { id: 'applied', label: 'Students applied', icon: UserPlus, count: pendingEnrollments.length },
      { id: 'subjects', label: 'Subjects & teachers', icon: BookOpen, count: 0 },
    ];

    return (
      <div className="space-y-6" data-testid="class-management">
        <Breadcrumb />

        {/* Class header */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center bg-blue-50 flex-shrink-0"><BookOpen className="w-6 h-6 text-blue-500" /></div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{selectedClass.name}</h3>
                <div className="flex items-center gap-3 mt-1.5 text-sm text-gray-400">
                  <span className="font-mono text-xs bg-gray-50 px-2 py-0.5 rounded-md">{selectedClass.code}</span>
                  <span>Capacity: {selectedClass.capacity}</span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-amber-50 border border-amber-100" data-testid="class-code-display">
                <Hash className="w-4 h-4 text-amber-600" />
                <span className="text-sm font-semibold font-mono text-amber-800">{selectedClass.class_code}</span>
                <button onClick={() => copyCode(selectedClass.class_code)} className="ml-1 p-1 rounded-lg hover:bg-amber-100 transition-colors" data-testid="copy-class-code">
                  <ClipboardCopy className="w-3.5 h-3.5 text-amber-600" />
                </button>
              </div>
              {pendingEnrollments.length > 0 && (
                <div className="flex items-center gap-1 px-3 py-2.5 rounded-xl bg-red-50 border border-red-100">
                  <AlertCircle className="w-4 h-4 text-red-500" />
                  <span className="text-sm font-semibold text-red-700">{pendingEnrollments.length} pending</span>
                </div>
              )}
            </div>
          </div>

          {/* Class Teacher Assignment */}
          <div className="mt-3 flex items-center gap-3 px-4 py-3 rounded-xl border border-gray-200 bg-white">
            <Users className="w-4 h-4 text-gray-400 flex-shrink-0" />
            {selectedClass.assigned_teacher_id ? (
              <>
                <button onClick={() => setTeacherInfoPopup({ name: selectedClass.assigned_teacher_name, email: selectedClass.assigned_teacher_email, id: selectedClass.assigned_teacher_id, assigned_at: selectedClass.teacher_assigned_at })} className="flex items-center gap-2 hover:bg-gray-50 rounded-lg px-1.5 py-1 -ml-1.5 transition-colors" data-testid="teacher-profile-click">
                  <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold text-white flex-shrink-0" style={{ background: A }}>{(selectedClass.assigned_teacher_name || '?')[0]}</div>
                  <div className="text-left"><p className="text-xs font-semibold text-gray-900">{selectedClass.assigned_teacher_name}</p><p className="text-xs text-gray-500">{selectedClass.assigned_teacher_email}</p></div>
                </button>
                <div className="flex-1" />
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-green-50 text-green-600">Class teacher</span>
                <Button onClick={() => { setShowSubModal(true); setSubStep(1); setSubSelected(null); setSubQuery(''); setSubResults([]); setSubStartDate(''); setSubEndDate(''); setSubReason(''); setSubConfirmPwd(''); setSubError(''); }} size="sm" className="h-7 px-3 rounded-lg text-xs border-blue-200 text-blue-600 hover:bg-blue-50" variant="outline" data-testid="assign-substitute-btn"><Shield className="w-3 h-3 mr-1" /> Assign substitute</Button>
                <button onClick={() => { setShowUnassign2FA(true); setUnassignPwd(''); }} className="text-xs text-red-400 hover:text-red-600 px-2 py-1 rounded-lg hover:bg-red-50" data-testid="unassign-class-teacher">Remove</button>
              </>
            ) : (
              <>
                <span className="text-xs text-gray-500 flex-1">No class teacher assigned</span>
                <Button onClick={() => { setShowClassTeacherModal(true); setClassTeacherQuery(''); }} size="sm" className="h-7 px-3 rounded-lg text-white text-xs" style={{ background: A }} data-testid="assign-class-teacher-btn"><UserPlus className="w-3 h-3 mr-1" /> Assign teacher</Button>
                <Button onClick={() => { setShowSubModal(true); setSubStep(1); setSubSelected(null); setSubQuery(''); setSubResults([]); setSubStartDate(''); setSubEndDate(''); setSubReason(''); setSubConfirmPwd(''); setSubError(''); }} size="sm" className="h-7 px-3 rounded-lg text-xs border-blue-200 text-blue-600 hover:bg-blue-50" variant="outline" data-testid="assign-substitute-btn"><Shield className="w-3 h-3 mr-1" /> Assign substitute</Button>
              </>
            )}
          </div>

          {/* Active Class Substitutions */}
          {classSubs.filter(s => s.status === 'active').length > 0 && (
            <div className="mt-2 space-y-2" data-testid="class-substitutes">
              {classSubs.filter(s => s.status === 'active').map(sub => (
                <div key={sub.id} className="flex items-center gap-3 px-4 py-3 rounded-xl border-2 border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50" data-testid={`class-sub-${sub.id}`}>
                  <Shield className="w-4 h-4 text-blue-500 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold text-white bg-blue-500">{(sub.substitute_teacher_name || '?')[0]}</div>
                      <p className="text-xs font-semibold text-gray-900">{sub.substitute_teacher_name}</p>
                      <span className="text-xs font-semibold px-1.5 py-0.5 rounded-full bg-blue-500 text-white">Substitute</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5 ml-8"><Calendar className="w-3 h-3 inline mr-1" />{sub.start_date} &mdash; {sub.end_date}{sub.reason && <span className="ml-2 text-gray-400">&middot; {sub.reason}</span>}</p>
                  </div>
                  <span className="text-xs text-blue-600 bg-blue-100 px-1.5 py-0.5 rounded font-medium">{sub.lectures_posted || 0} lectures</span>
                  <button onClick={() => { setShowUnassignSub2FA(sub); setUnassignSubPwd(''); }} className="text-xs text-red-400 hover:text-red-600 px-2 py-1 rounded-lg hover:bg-red-50" data-testid={`unassign-sub-${sub.id}`}>Remove</button>
                </div>
              ))}
            </div>
          )}

          {/* Past Class Substitutions */}
          {classSubs.filter(s => s.status !== 'active').length > 0 && (
            <div className="mt-2 space-y-1.5">
              <p className="text-xs font-semibold text-gray-400">Past substitutions</p>
              {classSubs.filter(s => s.status !== 'active').map(sub => (
                <div key={sub.id} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 border border-gray-100 opacity-60" data-testid={`past-sub-${sub.id}`}>
                  <Shield className="w-3 h-3 text-gray-400" />
                  <p className="text-xs text-gray-600 flex-1">{sub.substitute_teacher_name} &middot; {sub.start_date} &mdash; {sub.end_date}</p>
                  <span className="text-xs text-gray-400 px-1.5 py-0.5 rounded bg-gray-100">{sub.status}</span>
                  <button onClick={async () => { await api(`/class-substitutions/${sub.id}`, { method: 'DELETE' }); loadClassSubs(selectedClass.id); }} className="p-1 rounded hover:bg-red-50 text-gray-300 hover:text-red-500"><Trash2 className="w-3 h-3" /></button>
                </div>
              ))}
            </div>
          )}

          {/* Class Teacher Search Modal */}
          {showClassTeacherModal && (
            <div className="fixed inset-0 z-[60] flex items-center justify-center">
              <div className="absolute inset-0 bg-black/25 backdrop-blur-[2px]" onClick={() => setShowClassTeacherModal(false)} />
              <div className="relative bg-white rounded-2xl w-[400px] shadow-2xl border border-gray-100" data-testid="class-teacher-modal">
                <div className="p-5 pb-3">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-gray-900">Assign class teacher</h3>
                    <button onClick={() => setShowClassTeacherModal(false)} className="p-1 rounded-lg hover:bg-gray-100"><X className="w-4 h-4 text-gray-400" /></button>
                  </div>
                  <div className="relative">
                    <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <Input value={classTeacherQuery} onChange={e => setClassTeacherQuery(e.target.value)} placeholder="Search by name or email..." className="h-9 pl-9 rounded-lg text-sm" autoFocus data-testid="class-teacher-search" />
                  </div>
                </div>
                <div className="max-h-[250px] overflow-y-auto px-5 pb-4">
                  {classTeacherSearching ? <p className="text-xs text-gray-400 text-center py-4">Searching...</p>
                  : classTeacherResults.length === 0 ? <p className="text-xs text-gray-400 text-center py-4">{classTeacherQuery ? 'No results' : 'No teachers found'}</p>
                  : classTeacherResults.map(t => (
                    <div key={t.id} className="flex items-center gap-3 py-2.5 hover:bg-gray-50 rounded-lg px-2">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold text-white flex-shrink-0" style={{ background: A }}>{(t.full_name || '?')[0]}</div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-gray-900">{t.full_name}</p>
                        <p className="text-xs text-gray-500">{t.email}</p>
                        <p className="text-xs text-gray-400 font-mono">ID: {t.id}</p>
                      </div>
                      <button onClick={async () => {
                        setAssigningClassTeacher(t.id);
                        try {
                          await api(`/classes/${selectedClass.id}/assign-teacher`, { method: 'POST', body: JSON.stringify({ teacher_id: t.id }) });
                          const updated = await api(`/classes/${selectedClass.id}`);
                          if (updated) setSelectedClass(updated);
                          setShowClassTeacherModal(false); setAssigningClassTeacher(null);
                          toast.success(`${t.full_name} assigned as class teacher`);
                        } catch (e) { notifyError(e, "Couldn't assign the teacher."); setAssigningClassTeacher(null); }
                      }} disabled={assigningClassTeacher === t.id} className="h-7 px-2.5 rounded-lg text-xs font-semibold text-white" style={{ background: A }} data-testid={`pick-teacher-${t.id}`}>
                        {assigningClassTeacher === t.id ? '...' : 'Assign'}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Unassign 2FA Confirmation */}
          {showUnassign2FA && (
            <div className="fixed inset-0 z-[60] flex items-center justify-center">
              <div className="absolute inset-0 bg-black/25 backdrop-blur-[2px]" onClick={() => setShowUnassign2FA(false)} />
              <div className="relative bg-white rounded-2xl w-[360px] shadow-2xl border border-gray-100 p-5">
                <div className="flex items-center gap-2.5 mb-4">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-red-50"><Lock className="w-4 h-4 text-red-500" /></div>
                  <div><h3 className="text-sm font-semibold text-gray-900">Confirm removal</h3><p className="text-xs text-gray-500">Remove {selectedClass.assigned_teacher_name}</p></div>
                </div>
                <div className="mb-4">
                  <label className="block text-xs font-semibold text-gray-400 mb-1">Your password</label>
                  <Input type="password" value={unassignPwd} onChange={e => setUnassignPwd(e.target.value)} placeholder="Enter password to confirm" className="h-9 rounded-lg text-sm" autoFocus data-testid="unassign-pwd" />
                </div>
                <div className="flex gap-2">
                  <Button onClick={async () => {
                    if (!unassignPwd) return;
                    setUnassigning(true);
                    try {
                      const authRes = await verifyMyPassword(unassignPwd);
                      if (!authRes.ok) { notifyError(await authRes.json().catch(() => ({})), "Couldn't check your password."); setUnassigning(false); return; }
                      await api(`/classes/${selectedClass.id}/unassign-teacher`, { method: 'POST' });
                      const updated = await api(`/classes/${selectedClass.id}`);
                      if (updated) setSelectedClass(updated);
                      setShowUnassign2FA(false);
                      toast.success('Teacher removed');
                    } catch (e) { notifyError(e, "Couldn't remove the teacher."); }
                    setUnassigning(false);
                  }} disabled={unassigning || !unassignPwd} className="flex-1 h-9 rounded-lg text-white text-xs font-semibold bg-red-500 hover:bg-red-600 disabled:opacity-50" data-testid="confirm-unassign">{unassigning ? 'Removing...' : 'Confirm & Remove'}</Button>
                  <button onClick={() => setShowUnassign2FA(false)} className="text-xs text-gray-500 px-3">Cancel</button>
                </div>
              </div>
            </div>
          )}

          {/* ═══ Assign Substitute Modal (3-step) ═══ */}
          {showSubModal && (
            <div className="fixed inset-0 z-[60] flex items-center justify-center">
              <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={() => setShowSubModal(false)} />
              <div className="relative bg-white rounded-2xl p-6 w-[500px] shadow-2xl max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()} data-testid="inst-sub-modal">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center"><Shield className="w-5 h-5 text-blue-600" /></div>
                  <div className="flex-1">
                    <h3 className="text-base font-semibold text-gray-900">Assign substitute teacher</h3>
                    <p className="text-xs text-gray-500">Step {subStep} of 3</p>
                  </div>
                  <button onClick={() => setShowSubModal(false)} className="p-1.5 rounded-lg hover:bg-gray-100"><X className="w-4 h-4 text-gray-400" /></button>
                </div>
                <div className="flex gap-1 mb-5">{[1,2,3].map(s => (<div key={s} className="h-1 flex-1 rounded-full transition" style={{ background: s <= subStep ? '#1A7F37' : '#E5E7EB' }} />))}</div>

                {subStep === 1 && (
                  <>
                    <div className="relative mb-3">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input value={subQuery} onChange={e => {
                        setSubQuery(e.target.value);
                        setSubSearching(true);
                        api(`/teachers/search?q=${encodeURIComponent(e.target.value)}`).then(d => { setSubResults(d || []); setSubSearching(false); }).catch(() => setSubSearching(false));
                      }} placeholder="Search teacher..." className="h-10 pl-10 rounded-xl" autoFocus data-testid="inst-sub-search" />
                    </div>
                    <div className="flex-1 overflow-y-auto space-y-1 min-h-[150px] max-h-[280px]">
                      {subSearching ? <PersonResultsSkeleton />
                      : subResults.map(t => (
                        <button key={t.id} onClick={() => { setSubSelected(t); setSubStep(2); }} className="w-full flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-blue-50 text-left" data-testid={`inst-sub-t-${t.id}`}>
                          <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center text-sm font-semibold text-blue-700">{(t.full_name || '?')[0]}</div>
                          <div className="flex-1"><p className="text-sm font-semibold text-gray-900">{t.full_name}</p><p className="text-xs text-gray-500">{t.email} &middot; <span className="font-mono text-gray-400">{t.id}</span></p></div>
                          <ChevronRight className="w-4 h-4 text-gray-300" />
                        </button>
                      ))}
                    </div>
                  </>
                )}

                {subStep === 2 && subSelected && (
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 p-3 rounded-xl bg-blue-50 border border-blue-100">
                      <div className="w-10 h-10 rounded-full bg-blue-200 flex items-center justify-center text-sm font-semibold text-blue-800">{(subSelected.full_name || '?')[0]}</div>
                      <div className="flex-1"><p className="text-sm font-semibold text-gray-900">{subSelected.full_name}</p><p className="text-xs text-gray-500">{subSelected.email}</p></div>
                      <button onClick={() => { setSubSelected(null); setSubStep(1); }} className="p-1 rounded-lg hover:bg-blue-100 text-blue-400"><X className="w-4 h-4" /></button>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div><label className="text-xs font-semibold text-gray-500 block mb-1">Start Date *</label><Input type="date" value={subStartDate} onChange={e => setSubStartDate(e.target.value)} className="h-10 rounded-xl text-sm" data-testid="inst-sub-start" /></div>
                      <div><label className="text-xs font-semibold text-gray-500 block mb-1">End Date *</label><Input type="date" value={subEndDate} onChange={e => setSubEndDate(e.target.value)} className="h-10 rounded-xl text-sm" data-testid="inst-sub-end" /></div>
                    </div>
                    <div><label className="text-xs font-semibold text-gray-500 block mb-1">Reason (optional)</label><Input value={subReason} onChange={e => setSubReason(e.target.value)} placeholder="e.g., Teacher on leave" className="h-10 rounded-xl text-sm" /></div>
                    <Button onClick={() => setSubStep(3)} disabled={!subStartDate || !subEndDate} className="w-full h-10 rounded-xl text-white text-sm bg-blue-600 hover:bg-blue-700 disabled:opacity-50" data-testid="inst-sub-next">Continue to verification</Button>
                  </div>
                )}

                {subStep === 3 && subSelected && (
                  <div className="space-y-4">
                    <div className="bg-blue-50/50 rounded-xl p-4 border border-blue-100">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-9 h-9 rounded-full bg-blue-200 flex items-center justify-center text-sm font-semibold text-blue-800">{(subSelected.full_name || '?')[0]}</div>
                        <div><p className="text-sm font-semibold text-gray-900">{subSelected.full_name}</p><p className="text-xs text-gray-500">{subSelected.email}</p></div>
                      </div>
                      <p className="text-xs text-gray-600 ml-12"><Calendar className="w-3 h-3 inline mr-1" />{subStartDate} &mdash; {subEndDate}</p>
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-500 block mb-1 flex items-center gap-1"><Lock className="w-3 h-3" /> Your Password (2FA)</label>
                      <Input type="password" value={subConfirmPwd} onChange={e => setSubConfirmPwd(e.target.value)} placeholder="Enter your password" className="h-10 rounded-xl" autoFocus data-testid="inst-sub-pwd" onKeyDown={e => { if (e.key === 'Enter') document.querySelector('[data-testid="inst-sub-confirm"]')?.click(); }} />
                    </div>
                    {subError && <p className="text-xs text-red-500">{subError}</p>}
                    <div className="flex gap-2">
                      <button onClick={() => setSubStep(2)} className="h-10 px-4 rounded-xl text-sm text-gray-500 hover:bg-gray-50 border border-gray-200">Back</button>
                      <Button onClick={async () => {
                        if (!subConfirmPwd) return;
                        setSubAssigning(true); setSubError('');
                        try {
                          const authRes = await verifyMyPassword(subConfirmPwd);
                          if (!authRes.ok) { setSubError(friendlyError(await authRes.json().catch(() => ({})), "Couldn't check your password.")); setSubAssigning(false); return; }
                          const r = await api(`/classes/${selectedClass.id}/assign-substitute`, { method: 'POST', body: JSON.stringify({ substitute_teacher_id: subSelected.id, start_date: subStartDate, end_date: subEndDate, reason: subReason }) });
                          if (r.detail) { setSubError(friendlyError(r)); setSubAssigning(false); return; }
                          setShowSubModal(false);
                          toast.success(`${subSelected.full_name} assigned as substitute`);
                          loadClassSubs(selectedClass.id);
                        } catch (e) { setSubError(friendlyError(e, "Couldn't assign the substitute.")); }
                        setSubAssigning(false);
                      }} disabled={subAssigning || !subConfirmPwd} className="flex-1 h-10 rounded-xl text-white text-sm bg-blue-600 hover:bg-blue-700 disabled:opacity-50" data-testid="inst-sub-confirm">
                        {subAssigning ? <><RefreshCw className="w-4 h-4 animate-spin mr-2" /> Verifying...</> : <><Lock className="w-4 h-4 mr-2" /> Confirm &amp; Assign</>}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ═══ Unassign Substitute 2FA ═══ */}
          {showUnassignSub2FA && (
            <div className="fixed inset-0 z-[60] flex items-center justify-center">
              <div className="absolute inset-0 bg-black/25 backdrop-blur-[2px]" onClick={() => setShowUnassignSub2FA(null)} />
              <div className="relative bg-white rounded-2xl w-[400px] shadow-2xl border border-gray-100 p-5" data-testid="inst-unassign-sub-modal">
                <div className="flex items-center gap-2.5 mb-4">
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-red-50"><Shield className="w-4 h-4 text-red-500" /></div>
                  <div><h3 className="text-sm font-semibold text-gray-900">Unassign substitute</h3><p className="text-xs text-gray-500">Remove {showUnassignSub2FA.substitute_teacher_name}</p></div>
                </div>
                <div className="mb-4">
                  <label className="block text-xs font-semibold text-gray-400 mb-1 flex items-center gap-1"><Lock className="w-3 h-3" /> Your Password (2FA)</label>
                  <Input type="password" value={unassignSubPwd} onChange={e => setUnassignSubPwd(e.target.value)} placeholder="Enter password" className="h-9 rounded-lg text-sm" autoFocus data-testid="inst-unsub-pwd" onKeyDown={e => e.key === 'Enter' && document.querySelector('[data-testid="inst-unsub-confirm"]')?.click()} />
                </div>
                <div className="flex gap-2">
                  <Button onClick={async () => {
                    if (!unassignSubPwd) return;
                    setUnassigningSub(true);
                    try {
                      const r = await fetch(`${API_URL}/api/class-substitutions/${showUnassignSub2FA.id}/unassign`, {
                        method: 'POST', headers: { Authorization: `Bearer ${localStorage.getItem('desky_token')}`, 'Content-Type': 'application/json' },
                        body: JSON.stringify({ password: unassignSubPwd }),
                      });
                      if (!r.ok) { notifyError(await r.json().catch(() => ({})), "Couldn't remove the substitute."); setUnassigningSub(false); return; }
                      setShowUnassignSub2FA(null); toast.success('Substitute removed'); loadClassSubs(selectedClass.id);
                    } catch (e) { notifyError(e, "Couldn't remove the substitute."); }
                    setUnassigningSub(false);
                  }} disabled={unassigningSub || !unassignSubPwd} className="flex-1 h-9 rounded-lg text-white text-xs font-semibold bg-red-500 hover:bg-red-600 disabled:opacity-50" data-testid="inst-unsub-confirm">{unassigningSub ? 'Removing...' : 'Confirm & Unassign'}</Button>
                  <button onClick={() => setShowUnassignSub2FA(null)} className="text-xs text-gray-500 px-3">Cancel</button>
                </div>
              </div>
            </div>
          )}

          {/* Teacher Info Popup */}
          {teacherInfoPopup && (
            <ProfileCard
              person={{ full_name: teacherInfoPopup.name, email: teacherInfoPopup.email, id: teacherInfoPopup.id, assigned_at: teacherInfoPopup.assigned_at, phone: teacherInfoPopup.phone }}
              accent={A}
              role="Class Teacher"
              onClose={() => setTeacherInfoPopup(null)}
            />
          )}
          <div className="mt-4 p-3 rounded-xl bg-gray-50 flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0"><Hash className="w-4 h-4 text-amber-600" /></div>
            <div className="flex items-center gap-2 flex-1">
              <span className="font-semibold font-mono text-gray-700 text-sm">{selectedClass.class_code}</span>
              <button onClick={() => { navigator.clipboard?.writeText(selectedClass.class_code); toast.success('Code copied'); }} className="p-1 rounded hover:bg-gray-200"><ClipboardCopy className="w-3.5 h-3.5 text-gray-400" /></button>
            </div>
            <div className="relative group">
              <button className="p-1 rounded hover:bg-gray-200 text-gray-400" data-testid="class-code-info-inst"><Info className="w-3.5 h-3.5" /></button>
              <div className="hidden group-hover:block absolute right-0 bottom-full mb-1 w-56 bg-gray-900 text-white text-xs rounded-lg p-2.5 z-20 shadow-lg">
                Share this code with students so they can enroll from their dashboard.
              </div>
            </div>
          </div>
        </div>

        {/* Tab bar */}
        <div className="flex gap-1 bg-gray-100 rounded-xl p-1" data-testid="class-tabs">
          {CLASS_TABS.map(tab => (
            <button key={tab.id}
              onClick={() => {
                setClassTab(tab.id);
                if (tab.id === 'students') { loadClassStudents(selectedClass.id); loadPendingEnrollments(selectedClass.id); }
                if (tab.id === 'applied') { loadPendingEnrollments(selectedClass.id); }
              }}
              className={`flex-1 flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg text-sm font-medium transition duration-200 ${classTab === tab.id ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
              data-testid={`tab-${tab.id}`}>
              <tab.icon className="w-4 h-4" />
              {tab.label}
              {tab.count !== undefined && tab.count > 0 && (<span className="ml-1 text-xs font-semibold px-1.5 py-0.5 rounded-full" style={{ background: `${A}15`, color: A }}>{tab.count}</span>)}
            </button>
          ))}
        </div>

        {/* ─── STUDENTS TAB ─── */}
        {classTab === 'students' && (
          <div className="space-y-5" data-testid="students-tab">
            {selectedStudentProfile && <ProfileCard person={selectedStudentProfile} accent={A} role="Student" onClose={() => setSelectedStudentProfile(null)} />}

            {/* Add students toolbar */}
            <div className="flex items-center justify-between">
              <h4 className="text-base font-semibold text-gray-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Enrolled Students ({classStudents.length})
              </h4>
              <div className="flex items-center gap-2">
                <Button variant={addMode === 'email' ? 'default' : 'outline'}
                  onClick={() => { setAddMode(addMode === 'email' ? null : 'email'); loadAvailableStudents(selectedClass.id); }}
                  className={`h-9 px-4 rounded-xl text-xs font-semibold ${addMode === 'email' ? 'text-white' : ''}`}
                  style={addMode === 'email' ? { background: A } : {}} data-testid="add-by-email-btn">
                  <Mail className="w-3.5 h-3.5 mr-1" /> By email
                </Button>
                <Button variant={addMode === 'available' ? 'default' : 'outline'}
                  onClick={() => { setAddMode(addMode === 'available' ? null : 'available'); loadAvailableStudents(selectedClass.id); }}
                  className={`h-9 px-4 rounded-xl text-xs font-semibold ${addMode === 'available' ? 'text-white' : ''}`}
                  style={addMode === 'available' ? { background: A } : {}} data-testid="add-available-btn">
                  <UserPlus className="w-3.5 h-3.5 mr-1" /> Available
                </Button>
                <Button variant={addMode === 'csv' ? 'default' : 'outline'}
                  onClick={() => setAddMode(addMode === 'csv' ? null : 'csv')}
                  className={`h-9 px-4 rounded-xl text-xs font-semibold ${addMode === 'csv' ? 'text-white' : ''}`}
                  style={addMode === 'csv' ? { background: A } : {}} data-testid="add-csv-btn">
                  <Upload className="w-3.5 h-3.5 mr-1" /> CSV
                </Button>
              </div>
            </div>

            {/* Student search bar */}
            {classStudents.length > 0 && !addMode && (
              <Card className="!p-3" data-testid="class-student-search">
                <div className="flex items-center gap-3">
                  <div className="relative flex-1 min-w-[180px]">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input value={classStudentSearch} onChange={e => setClassStudentSearch(e.target.value)} placeholder="Search enrolled students..." className="pl-9 h-9 text-sm rounded-lg" data-testid="class-student-search-input" />
                  </div>
                  <span className="text-xs text-gray-400">{filteredClassStudents.length} of {classStudents.length}</span>
                </div>
              </Card>
            )}

            {/* Add by Email form */}
            {addMode === 'email' && (
              <div className="bg-white rounded-2xl border-2 border-dashed p-5 space-y-3" style={{ borderColor: `${A}40` }} data-testid="add-email-form">
                <h5 className="text-sm font-semibold text-gray-700">Add students by email</h5>
                <p className="text-xs text-gray-400">Enter registered Deskey student emails, separated by commas or new lines.</p>
                <textarea value={emailInput} onChange={e => setEmailInput(e.target.value)} placeholder="student1@school.edu, student2@school.edu" className="w-full h-24 rounded-xl border border-gray-200 text-sm p-3 focus:outline-none focus:ring-2 resize-none" style={{ '--tw-ring-color': A }} data-testid="email-textarea" />
                <div className="flex gap-2">
                  <Button onClick={addStudentsByEmail} disabled={enrollLoading} className="h-9 px-5 rounded-xl text-white text-xs font-semibold" style={{ background: A }} data-testid="submit-emails-btn">
                    {enrollLoading ? 'Adding...' : 'Add Students'}
                  </Button>
                  <Button variant="ghost" onClick={() => { setAddMode(null); setEmailInput(''); }} className="h-9 px-4 rounded-xl text-xs text-gray-500">Cancel</Button>
                </div>
              </div>
            )}

            {/* Add from available students */}
            {addMode === 'available' && (
              <div className="bg-white rounded-2xl border-2 border-dashed p-5 space-y-3" style={{ borderColor: `${A}40` }} data-testid="add-available-form">
                <h5 className="text-sm font-semibold text-gray-700">Select from available students</h5>
                <p className="text-xs text-gray-400">Students registered in your institution who aren't enrolled in this class.</p>
                {availableStudents.length === 0 ? (
                  <p className="text-xs text-gray-400 py-3">No available students found.</p>
                ) : (
                  <div className="max-h-48 overflow-y-auto space-y-1 rounded-xl border border-gray-100 p-2">
                    {availableStudents.map(s => {
                      const selected = selectedAvailable.some(x => x.id === s.id);
                      return (
                        <button key={s.id} onClick={() => setSelectedAvailable(prev => selected ? prev.filter(x => x.id !== s.id) : [...prev, s])}
                          className={`w-full flex items-center gap-3 p-2.5 rounded-lg text-left transition-colors ${selected ? 'bg-green-50 border border-green-200' : 'hover:bg-gray-50'}`} data-testid={`avail-student-${s.id}`}>
                          <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 ${selected ? 'border-green-500 bg-green-500' : 'border-gray-300'}`}>
                            {selected && <CheckCircle className="w-3 h-3 text-white" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">{s.full_name}</p>
                            <p className="text-xs text-gray-400 truncate">{s.email}</p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
                {selectedAvailable.length > 0 && (
                  <div className="flex items-center gap-2">
                    <Button onClick={addSelectedStudents} disabled={enrollLoading} className="h-9 px-5 rounded-xl text-white text-xs font-semibold" style={{ background: A }} data-testid="submit-available-btn">
                      {enrollLoading ? 'Adding...' : `Add ${selectedAvailable.length} Students`}
                    </Button>
                    <Button variant="ghost" onClick={() => { setAddMode(null); setSelectedAvailable([]); }} className="h-9 px-4 rounded-xl text-xs text-gray-500">Cancel</Button>
                  </div>
                )}
              </div>
            )}

            {/* Add by CSV */}
            {addMode === 'csv' && (
              <div className="bg-white rounded-2xl border-2 border-dashed p-5 space-y-3" style={{ borderColor: `${A}40` }} data-testid="add-csv-form">
                <h5 className="text-sm font-semibold text-gray-700">Import from CSV</h5>
                <p className="text-xs text-gray-400">Paste CSV content with student emails. The system will find emails in any column.</p>
                <textarea value={csvInput} onChange={e => setCsvInput(e.target.value)} placeholder="name,email&#10;John Doe,john@school.edu&#10;Jane Smith,jane@school.edu" className="w-full h-28 rounded-xl border border-gray-200 text-sm p-3 font-mono focus:outline-none focus:ring-2 resize-none" style={{ '--tw-ring-color': A }} data-testid="csv-textarea" />
                <div className="flex gap-2">
                  <Button onClick={addStudentsByCSV} disabled={enrollLoading} className="h-9 px-5 rounded-xl text-white text-xs font-semibold" style={{ background: A }} data-testid="submit-csv-btn">
                    {enrollLoading ? 'Processing...' : 'Import Students'}
                  </Button>
                  <Button variant="ghost" onClick={() => { setAddMode(null); setCsvInput(''); }} className="h-9 px-4 rounded-xl text-xs text-gray-500">Cancel</Button>
                </div>
              </div>
            )}

            {/* Enrolled students list */}
            {opening ? <StudentTableSkeleton /> : classStudents.length === 0 && !addMode ? (
              <div className="bg-white rounded-2xl border-2 border-dashed border-gray-200 text-center py-16">
                <div className="w-14 h-14 rounded-2xl mx-auto mb-4 flex items-center justify-center bg-green-50"><GraduationCap className="w-7 h-7 text-green-500" /></div>
                <h4 className="text-base font-semibold text-gray-800">No students enrolled</h4>
                <p className="text-xs text-gray-400 mt-1.5 max-w-sm mx-auto">Add students by email, select from available students, import from CSV, or share the class code for self-enrollment.</p>
              </div>
            ) : classStudents.length > 0 && (
              <Card className="overflow-hidden !p-0" data-testid="enrolled-students-list">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50/80 border-b border-gray-100">
                    <tr>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500">Student</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500">Email</th>
                      <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredClassStudents.map(s => (
                      <tr key={s.id} className="border-b border-gray-50 hover:bg-gray-50/60 transition-colors" data-testid={`student-row-${s.student_id}`}>
                        <td className="px-5 py-3.5">
                          <button onClick={() => setSelectedStudentProfile({ full_name: s.student_name, email: s.student_email, student_id: s.student_id, ...s })} className="flex items-center gap-3 hover:opacity-80 transition-opacity" data-testid={`student-click-${s.student_id}`}>
                            <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold text-white flex-shrink-0" style={{ background: `${A}cc` }}>{(s.student_name || '?')[0].toUpperCase()}</div>
                            <span className="text-sm font-medium text-gray-900 truncate">{s.student_name}</span>
                          </button>
                        </td>
                        <td className="px-5 py-3.5 text-sm text-gray-500">{s.student_email}</td>
                        <td className="px-5 py-3.5">
                          <div className="flex items-center justify-end gap-2">
                            <button onClick={() => removeStudent(s.student_id)} className="text-xs text-red-500 hover:text-red-700 font-medium hover:underline transition-colors" data-testid={`remove-student-${s.student_id}`}>
                              Remove
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filteredClassStudents.length === 0 && classStudents.length > 0 && (
                  <div className="text-center py-8">
                    <p className="text-sm text-gray-400">No students match your search</p>
                  </div>
                )}
              </Card>
            )}
          </div>
        )}

        {/* ─── STUDENTS APPLIED TAB ─── */}
        {classTab === 'applied' && (
          <div className="space-y-5" data-testid="applied-tab">
            <h4 className="text-base font-semibold text-gray-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Students Waiting for Approval ({pendingEnrollments.length})
            </h4>
            {pendingEnrollments.length === 0 ? (
              <div className="bg-white rounded-2xl border-2 border-dashed border-gray-200 text-center py-16">
                <div className="w-14 h-14 rounded-2xl mx-auto mb-4 flex items-center justify-center bg-amber-50"><UserPlus className="w-7 h-7 text-amber-500" /></div>
                <h4 className="text-base font-semibold text-gray-800">No pending applications</h4>
                <p className="text-xs text-gray-400 mt-1.5 max-w-sm mx-auto">When students apply to join this class using the class code, their requests will appear here for your approval.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {pendingEnrollments.map(e => (
                  <div key={e.id} className="bg-white rounded-2xl border border-gray-100 p-5 flex items-center justify-between" data-testid={`applied-${e.id}`}>
                    <div className="flex items-center gap-4">
                      <div className="w-11 h-11 rounded-full flex items-center justify-center text-sm font-semibold text-white" style={{ background: A }}>{(e.student_name || '?')[0]}</div>
                      <div>
                        <p className="text-sm font-semibold text-gray-900">{e.student_name}</p>
                        <p className="text-xs text-gray-400 mt-0.5">{e.student_email}</p>
                        {e.requested_at && <p className="text-xs text-gray-400 mt-1">Applied {new Date(e.requested_at).toLocaleDateString()}</p>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button onClick={() => handleEnrollmentAction(e.id, 'approve')} className="h-9 px-5 rounded-xl text-xs font-semibold text-white" style={{ background: A }} data-testid={`approve-${e.id}`}>
                        <CheckCircle className="w-3.5 h-3.5 mr-1.5" /> Approve
                      </Button>
                      <Button onClick={() => handleEnrollmentAction(e.id, 'reject')} variant="outline" className="h-9 px-5 rounded-xl text-xs font-semibold text-red-500 border-red-200 hover:bg-red-50" data-testid={`reject-${e.id}`}>
                        <XCircle className="w-3.5 h-3.5 mr-1.5" /> Reject
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ─── SUBJECTS & TEACHERS TAB ─── */}
        {classTab === 'subjects' && (
          <ClassSubjectsTab classId={selectedClass.id} accent={A} />
        )}

      </div>

    );
  }

  return null;
}
